import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import API from "../../config/config";

const VerifyBankAccount = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    accountNumber: "",
    ifsc: "",
    beneficiaryName: "",
    bankName: "",
    consent: true,
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchSavedBankDetails = async () => {
      const token = localStorage.getItem("userPublicToken");

      try {
        const response = await fetch(API.ADMIN.USERDASHBOARD, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json();
        if (response.ok && result.success && result.data?.bankAccountDetails) {
          const bankDetails = result.data.bankAccountDetails;

          setFormData((prev) => ({
            ...prev,
            accountNumber: bankDetails.account_number || "",
            ifsc: bankDetails.ifsc || "",
            beneficiaryName: bankDetails.beneficiaryName || "",
            bankName: bankDetails.bankName || "",
          }));
        }
      } catch (err) {
        console.error("Failed to load previous bank details", err);
      }
    };

    fetchSavedBankDetails();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("userPublicToken");

      const payload = {
        accountNumber: formData.accountNumber,
        ifsc: formData.ifsc,
        consent: true,
        consentText: "I give my consent to verify my bank account.",
        beneficiaryName: formData.beneficiaryName,
        bankName: formData.bankName,
      };

      const response = await fetch(API.ADMIN.VERIFY_BANK_ACCOUNT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage({
          type: result.success ? "success" : "info",
          text: result.message,
        });
        if (result.success) {
          setTimeout(() => navigate("/user-dashboard"), 1500);
        }
      } else {
        setMessage({
          type: "error",
          text: result.message || "Verification failed.",
        });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: "An error occurred while verifying the bank account.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center items-center p-6">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-8 rounded-lg shadow-lg w-full max-w-lg space-y-6"
      >
        <button
          onClick={() => navigate(-1)}
          type="button"
          className="flex items-center text-gray-600 hover:text-indigo-600"
        >
          <ArrowLeft className="w-5 h-5 mr-1" />
          Back
        </button>
        <div className="flex items-center justify-center mb-4">
          <h2 className="text-xl font-semibold text-indigo-700 text-center">
            Verify Bank Details
          </h2>
        </div>

        {/* Account Holder Name */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">
            Account Holder Name
          </label>
          <input
            type="text"
            name="beneficiaryName"
            required
            value={formData.beneficiaryName}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Account Number */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">
            Account Number
          </label>
          <input
            type="text"
            name="accountNumber"
            required
            value={formData.accountNumber}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* IFSC Code */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">
            IFSC Code
          </label>
          <input
            type="text"
            name="ifsc"
            required
            value={formData.ifsc}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Bank Name */}
        <div>
          <label className="block mb-1 font-medium text-gray-700">
            Bank Name
          </label>
          <input
            type="text"
            name="bankName"
            required
            value={formData.bankName}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Message */}
        {message && (
          <div
            className={`p-3 rounded text-sm ${
              message.type === "success"
                ? "bg-green-100 text-green-700"
                : message.type === "info"
                ? "bg-yellow-100 text-yellow-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2 px-4 text-white rounded-lg transition ${
            loading
              ? "bg-indigo-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700"
          }`}
        >
          {loading ? "Submitting..." : "Submit Bank Account"}
        </button>
      </form>
    </div>
  );
};

export default VerifyBankAccount;
